import React from 'react';
import { Banknote, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { mockLoans, formatCurrency } from '@/utils/mockData';
import { toast } from 'sonner';

export default function LoansPage() {
  const [loanAmount, setLoanAmount] = React.useState(100000);
  const [tenure, setTenure] = React.useState(6);

  const monthlyPayment = (loanAmount * (1 + 0.055)) / tenure;
  const eligibleAmount = 500000;

  const handleApply = (e) => {
    e.preventDefault();
    toast.success('Loan application submitted! You will receive feedback within 24 hours.');
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="loans-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold font-heading text-slate-900">Loans</h1>
        <p className="text-slate-600 mt-1">Access quick loans with flexible repayment plans</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
              <Banknote className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Eligible Amount</p>
              <p className="text-2xl font-bold font-heading text-slate-900">{formatCurrency(eligibleAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Active Loans</p>
              <p className="text-2xl font-bold font-heading text-slate-900">1</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-brand-blue flex items-center justify-center">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Credit Score</p>
              <p className="text-2xl font-bold font-heading text-slate-900">720</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-xl font-bold font-heading text-slate-900 mb-6">Apply for Loan</h3>
          <form onSubmit={handleApply} className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Loan Amount</Label>
                <span className="text-2xl font-bold text-brand-blue">{formatCurrency(loanAmount)}</span>
              </div>
              <Slider value={[loanAmount]} onValueChange={(val) => setLoanAmount(val[0])} max={eligibleAmount} min={10000} step={10000} className="w-full" data-testid="loan-amount-slider" />
              <p className="text-sm text-slate-600">Max eligible: {formatCurrency(eligibleAmount)}</p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Repayment Period</Label>
                <span className="text-lg font-bold text-slate-900">{tenure} months</span>
              </div>
              <Slider value={[tenure]} onValueChange={(val) => setTenure(val[0])} max={12} min={3} step={1} className="w-full" data-testid="tenure-slider" />
            </div>

            <div className="bg-blue-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Monthly Payment</span>
                <span className="font-bold text-slate-900">{formatCurrency(monthlyPayment)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Interest Rate</span>
                <span className="font-bold text-slate-900">5.5% per annum</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Total Repayment</span>
                <span className="font-bold text-brand-blue">{formatCurrency(monthlyPayment * tenure)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="purpose">Loan Purpose</Label>
              <Input id="purpose" placeholder="e.g., Business expansion" data-testid="loan-purpose-input" required className="h-12 rounded-lg" />
            </div>

            <Button type="submit" data-testid="apply-loan-btn" className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95">
              Apply Now
            </Button>
          </form>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-xl font-bold font-heading text-slate-900 mb-4">Loan History</h3>
            <div className="space-y-4">
              {mockLoans.map((loan) => (
                <div key={loan.id} data-testid={`loan-${loan.id}`} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-slate-900">{loan.type}</p>
                      <p className="text-sm text-slate-600">{formatCurrency(loan.amount)} loan</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${loan.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-700'}`}>
                      {loan.status}
                    </span>
                  </div>
                  {loan.status === 'active' && (
                    <>
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">Balance</span>
                          <span className="font-semibold text-slate-900">{formatCurrency(loan.balance)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">Next Payment</span>
                          <span className="font-semibold text-slate-900">{formatCurrency(loan.monthlyPayment)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">Due Date</span>
                          <span className="font-semibold text-slate-900">{loan.dueDate}</span>
                        </div>
                      </div>
                      <Button size="sm" data-testid={`repay-btn-${loan.id}`} className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-lg transition-all active:scale-95">
                        Make Payment
                      </Button>
                    </>
                  )}
                  {loan.status === 'completed' && (
                    <p className="text-sm text-slate-600">Completed on Nov 30, 2024</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div className="space-y-2">
                <h4 className="font-bold text-lg">Improve Your Credit Score</h4>
                <p className="text-blue-100 text-sm leading-relaxed">Make timely repayments and maintain low balances to qualify for higher loan amounts</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
